import { useState, useEffect, useMemo, ReactNode } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Edit,
  Trash2,
  Apple,
  Utensils,
  ClipboardList,
  TrendingUp,
  Loader2,
  Building2,
  LineChart as LineChartIcon,
  Scale,
  ChevronDown,
  Ruler,
  Calculator,
  Activity,
  Calendar,
  Printer,
  Stethoscope,
  HeartPulse,
  Clock,
  Link2,
  CalendarX,
  CheckCircle2,
  X,
  Upload,
  FileSpreadsheet,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { NoGymSelected } from '@/components/NoGymSelected';
import { gymApi, nutritionApi } from '@/lib/api';
import { NutritionPlan, NutritionMeal, NutritionProgress, NutritionMealType, NutritionClinicalHistory, NutritionAppointment, NutritionMealOption, NutritionAvailability, NutritionAvailabilityBlock } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { setPageArea, reportError } from '@/lib/sentry';
import { NutritionMonthCalendar } from '@/components/NutritionMonthCalendar';
import { NutritionAvailabilityDialog } from '@/components/NutritionAvailabilityDialog';
import { ExcelImportWizard, ImportKindConfig, ImportResult } from '@/components/ExcelImportWizard';
import {
  SKINFOLD_SITES,
  PERIMETER_SITES,
  DIAMETER_SITES,
  ACTIVITY_LABELS,
  FORMULA_LABELS,
  GOAL_LABELS,
  Sex,
  ActivityLevel,
  CompFormula,
  DensityEquation,
  EnergyGoal,
  SiteDef,
} from '@/lib/nutrition-formulas';
import { computeProgressResults } from '@/lib/nutrition-progress-calc';
import {
  NUTRITION_PROGRESS_FIELDS,
  buildNutritionProgressPayload,
  findClientsByName,
  parseNutritionProgressRows,
} from '@/lib/nutrition-import-utils';
import { phonesMatch } from '@/lib/phone-utils';

const MEAL_TYPES: { value: NutritionMealType; label: string }[] = [
  { value: 'desayuno', label: 'Desayuno' },
  { value: 'media_manana', label: 'Media Mañana' },
  { value: 'almuerzo', label: 'Almuerzo' },
  { value: 'media_tarde', label: 'Media Tarde' },
  { value: 'cena', label: 'Cena' },
  { value: 'snack', label: 'Snack' },
];

const SEX_OPTIONS: { value: Sex; label: string }[] = [
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Femenino' },
];

const DENSITY_OPTIONS: { value: DensityEquation; label: string }[] = [
  { value: 'siri', label: 'Siri' },
  { value: 'brozek', label: 'Brozek' },
];

const APPT_STATUS: Record<string, string> = {
  scheduled: 'Programada',
  completed: 'Asistió',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
};

const escapeHtml = (v: any) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const mealTypeLabel = (type: string) =>
  MEAL_TYPES.find((m) => m.value === type)?.label || type;

const num = (v: any) => (v === '' || v === null || v === undefined ? null : Number(v));
const fmt = (v: any, unit = '') => (v === null || v === undefined || v === '' ? '—' : `${v}${unit}`);
const fmtDelta = (v: number | null, unit = '') => {
  if (v === null || Number.isNaN(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${Math.round(v * 100) / 100}${unit}`;
};

const emptySites = (sites: SiteDef[]) =>
  sites.reduce((acc, s) => ({ ...acc, [s.key]: '' }), {} as Record<string, string>);

const toNumRecord = (vals: Record<string, string>): Record<string, number | null> => {
  const out: Record<string, number | null> = {};
  for (const [k, v] of Object.entries(vals || {})) {
    const n = num(v);
    if (n !== null && !Number.isNaN(n)) out[k] = n;
  }
  return out;
};

interface MealOptionForm {
  name: string;
  portion: string;
  kcal: string;
  protein: string;
  carbs: string;
  fat: string;
}

const emptyMealOption = (): MealOptionForm => ({ name: '', portion: '', kcal: '', protein: '', carbs: '', fat: '' });

const EMPTY_MEAL_FORM = {
  meal_type: 'desayuno' as NutritionMealType,
  name: '',
  description: '',
  calories: '',
  protein: '',
  carbs: '',
  fat: '',
  percent_of_target: '',
  options: [] as MealOptionForm[],
};

const mealOptionsToForm = (options?: NutritionMealOption[] | null): MealOptionForm[] =>
  (options || []).map((o) => ({
    name: o.name || '',
    portion: o.portion != null ? String(o.portion) : '',
    kcal: o.kcal != null ? String(o.kcal) : '',
    protein: o.protein != null ? String(o.protein) : '',
    carbs: o.carbs != null ? String(o.carbs) : '',
    fat: o.fat != null ? String(o.fat) : '',
  }));

const formOptionsToPayload = (options: MealOptionForm[]): NutritionMealOption[] =>
  (options || [])
    .filter((o) => (o.name || '').trim() !== '' || (o.portion || '').trim() !== '')
    .map((o) => ({
      name: (o.name || '').trim(),
      portion: (o.portion || '').trim() || null,
      kcal: num(o.kcal),
      protein: num(o.protein),
      carbs: num(o.carbs),
      fat: num(o.fat),
    }));

function Section({
  title,
  icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border border-border/60">
      <CollapsibleTrigger className="flex w-full items-center justify-between p-3 text-sm font-semibold">
        <span className="flex items-center gap-2">
          {icon}
          {title}
        </span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border/60 p-3 space-y-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function NutritionPage() {
  const { gym, hasRole } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = hasRole(['super_admin']);
  const canManagePlans = hasRole(['gym_admin', 'gym_supervisor']);
  const canManageProgress = hasRole(['gym_admin', 'gym_supervisor', 'gym_staff', 'staff']);

  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab && ['planes', 'progreso', 'clinico', 'agenda', 'importar'].includes(tab) ? tab : 'planes';
  });  const [plans, setPlans] = useState<NutritionPlan[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Plan dialog
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<NutritionPlan | null>(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    objective: '',
    description: '',
    client_id: '',
    target_calories: '',
    target_protein: '',
    target_carbs: '',
    target_fat: '',
    start_date: '',
    end_date: '',
    notes: '',
  });

  // Meals dialog
  const [isMealsDialogOpen, setIsMealsDialogOpen] = useState(false);
  const [mealPlan, setMealPlan] = useState<NutritionPlan | null>(null);
  const [editingMeal, setEditingMeal] = useState<NutritionMeal | null>(null);
  const [mealForm, setMealForm] = useState({
    meal_type: 'desayuno' as NutritionMealType,
    name: '',
    description: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    percent_of_target: '',
    options: [] as MealOptionForm[],
  });

  // Progress / evaluation
  const [selectedClientId, setSelectedClientId] = useState('');
  const [progress, setProgress] = useState<NutritionProgress[]>([]);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const [editingProgress, setEditingProgress] = useState<NutritionProgress | null>(null);
  const [progressForm, setProgressForm] = useState({
    recorded_at: new Date().toISOString().slice(0, 10),
    sex: '' as '' | Sex,
    age_years: '',
    weight_kg: '',
    height_cm: '',
    sitting_height_cm: '',
    activity_level: 'moderate' as ActivityLevel,
    formula: 'jp7' as CompFormula,
    density_equation: 'siri' as DensityEquation,
    goal: 'maintenance' as EnergyGoal,
    body_fat_manual: '',
    muscle_kg: '',
    visceral_fat_manual: '',
    waist_cm: '',
    hip_cm: '',
    chest_cm: '',
    arm_cm: '',
    thigh_cm: '',
    adherence_score: '',
    notes: '',
    skinfolds: emptySites(SKINFOLD_SITES),
    perimeters: emptySites(PERIMETER_SITES),
    diameters: emptySites(DIAMETER_SITES),
  });
  const [targets, setTargets] = useState({
    target_calories: '',
    target_protein: '',
    target_carbs: '',
    target_fat: '',
  });

  // Clinical history
  const [clinical, setClinical] = useState<NutritionClinicalHistory | null>(null);
  const [clinicalForm, setClinicalForm] = useState({
    allergies: '',
    intolerances: '',
    pathologies: '',
    medications: '',
    supplements: '',
    activity_level: 'moderate',
    goal: '',
    observations: '',
  });
  const [isSavingClinical, setIsSavingClinical] = useState(false);

  // Appointments
  const [appointments, setAppointments] = useState<NutritionAppointment[]>([]);
  const [isApptDialogOpen, setIsApptDialogOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState<NutritionAppointment | null>(null);
  const [apptForm, setApptForm] = useState({
    client_id: '',
    scheduled_at: '',
    type: '',
    status: 'scheduled',
    notes: '',
  });

  // Agenda de reservas (disponibilidad, bloqueos, enlace público)
  const [availability, setAvailability] = useState<NutritionAvailability[]>([]);
  const [availabilityBlocks, setAvailabilityBlocks] = useState<NutritionAvailabilityBlock[]>([]);
  const [isAvailabilityOpen, setIsAvailabilityOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [cancelTarget, setCancelTarget] = useState<NutritionAppointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    setPageArea('nutrition');
  }, []);

  useEffect(() => {
    fetchData();
  }, [gym?.gym_id]);

  useEffect(() => {
    loadAvailability();
  }, [gym?.gym_id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const gId = gym?.gym_id || undefined;
      const [plansRes, clientsRes, apptRes] = await Promise.all([
        nutritionApi.listPlans(gId),
        gymApi.listClients(null, gId),
        nutritionApi.listAppointments(gId),
      ]);
      if (plansRes.success) setPlans(plansRes.plans || []);
      if (clientsRes.success) setClients(clientsRes.clients || []);
      if (apptRes.success) setAppointments(apptRes.appointments || []);
    } catch (error) {
      reportError(error, { operation: 'nutrition_load', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo cargar la información de nutrición.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const activeClients = useMemo(
    () => clients.filter((c) => (c.status || '').toLowerCase() === 'active'),
    [clients]
  );

  const nutritionImportKinds = useMemo<ImportKindConfig[]>(
    () => [
      {
        key: 'nutrition_progress',
        label: 'Evaluaciones de nutrición',
        description: 'Peso, medidas, pliegues y métricas calculadas por clienta.',
        icon: FileSpreadsheet,
        fields: NUTRITION_PROGRESS_FIELDS,
        supportsMode: true,
        templateKind: 'nutrition_progress',
        importNote:
          'Si ya existe una evaluación de la misma clienta y fecha, "Solo crear nuevas" la omite y "Crear y actualizar" la reemplaza.',
        parse: (rows, mapping, rawTypedRows) =>
          parseNutritionProgressRows(rows, mapping, rawTypedRows, {
            resolveClient: (phone, fullName) => {
              if (phone) {
                const matches = clients.filter((client) => phonesMatch(client.phone, phone));
                if (matches.length === 1) return { client: matches[0] };
                if (matches.length > 1) {
                  return { client: null, error: 'Hay varias clientas con ese teléfono; corrige los duplicados' };
                }
              }
              if (fullName) {
                const matches = findClientsByName(clients, fullName);
                if (matches.length === 1) return { client: matches[0] };
                if (matches.length > 1) {
                  return { client: null, error: 'Hay varias clientas con ese nombre; usa el teléfono' };
                }
              }
              return { client: null };
            },
          }),
        buildPayload: buildNutritionProgressPayload,
      },
    ],
    [clients]
  );

  const stats = useMemo(() => ({
    total: plans.length,
    templates: plans.filter((p) => !p.client_id).length,
    assigned: plans.filter((p) => !!p.client_id).length,
    meals: plans.reduce((sum, p) => sum + (p.meals?.length || 0), 0),
  }), [plans]);

  // ---- Plans ----
  const openNewPlan = () => {
    setEditingPlan(null);
    setPlanForm({
      name: '', objective: '', description: '', client_id: '',
      target_calories: '', target_protein: '', target_carbs: '', target_fat: '',
      start_date: '', end_date: '', notes: '',
    });
    setIsPlanDialogOpen(true);
  };

  const openEditPlan = (plan: NutritionPlan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || '',
      objective: plan.objective || '',
      description: plan.description || '',
      client_id: plan.client_id || '',
      target_calories: plan.target_calories != null ? String(plan.target_calories) : '',
      target_protein: plan.target_protein != null ? String(plan.target_protein) : '',
      target_carbs: plan.target_carbs != null ? String(plan.target_carbs) : '',
      target_fat: plan.target_fat != null ? String(plan.target_fat) : '',
      start_date: plan.start_date || '',
      end_date: plan.end_date || '',
      notes: plan.notes || '',
    });
    setIsPlanDialogOpen(true);
  };

  const savePlan = async () => {
    if (!planForm.name.trim()) {
      toast({ title: 'Nombre requerido', description: 'El plan necesita un nombre.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        name: planForm.name.trim(),
        objective: planForm.objective || null,
        description: planForm.description || null,
        client_id: planForm.client_id || null,
        target_calories: num(planForm.target_calories),
        target_protein: num(planForm.target_protein),
        target_carbs: num(planForm.target_carbs),
        target_fat: num(planForm.target_fat),
        start_date: planForm.start_date || null,
        end_date: planForm.end_date || null,
        notes: planForm.notes || null,
      };
      if (editingPlan) {
        await nutritionApi.updatePlan(editingPlan.id, payload, gym?.gym_id || undefined);
        toast({ title: 'Plan actualizado' });
      } else {
        await nutritionApi.createPlan(payload, gym?.gym_id || undefined);
        toast({ title: 'Plan creado' });
      }
      setIsPlanDialogOpen(false);
      await fetchData();
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_plan', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar el plan.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const deletePlan = async (plan: NutritionPlan) => {
    if (!window.confirm(`¿Eliminar el plan "${plan.name}"? Se perderán sus comidas.`)) return;
    try {
      await nutritionApi.deletePlan(plan.id, gym?.gym_id || undefined);
      toast({ title: 'Plan eliminado' });
      await fetchData();
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_plan', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar el plan.', variant: 'destructive' });
    }
  };

  // ---- Meals ----
  const openMeals = (plan: NutritionPlan) => {
    setMealPlan(plan);
    setEditingMeal(null);
    setMealForm({ ...EMPTY_MEAL_FORM, options: [] });
    setIsMealsDialogOpen(true);
  };

  const refreshMealPlan = async (planId: string) => {
    const refreshed = await nutritionApi.listPlans(gym?.gym_id || undefined);
    const updated = (refreshed.plans || []).find((p: NutritionPlan) => p.id === planId);
    if (updated) setMealPlan(updated);
  };

  const startMeal = (meal: NutritionMeal) => {
    setEditingMeal(meal);
    setMealForm({
      meal_type: meal.meal_type,
      name: meal.name,
      description: meal.description || '',
      calories: meal.calories != null ? String(meal.calories) : '',
      protein: meal.protein != null ? String(meal.protein) : '',
      carbs: meal.carbs != null ? String(meal.carbs) : '',
      fat: meal.fat != null ? String(meal.fat) : '',
      percent_of_target: meal.percent_of_target != null ? String(meal.percent_of_target) : '',
      options: mealOptionsToForm(meal.options),
    });
  };

  const resetMealForm = () => {
    setEditingMeal(null);
    setMealForm({ ...EMPTY_MEAL_FORM, options: [] });
  };

  const saveMeal = async () => {
    if (!mealPlan) return;
    if (!mealForm.name.trim()) {
      toast({ title: 'Nombre requerido', description: 'La comida necesita un nombre.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        plan_id: mealPlan.id,
        meal_type: mealForm.meal_type,
        name: mealForm.name.trim(),
        description: mealForm.description || null,
        calories: num(mealForm.calories),
        protein: num(mealForm.protein),
        carbs: num(mealForm.carbs),
        fat: num(mealForm.fat),
        percent_of_target: num(mealForm.percent_of_target),
        options: formOptionsToPayload(mealForm.options),
        sort_order: editingMeal?.sort_order ?? (mealPlan.meals?.length || 0),
      };
      if (editingMeal) {
        await nutritionApi.updateMeal(editingMeal.id, payload, gym?.gym_id || undefined);
        toast({ title: 'Comida actualizada' });
      } else {
        await nutritionApi.addMeal(payload, gym?.gym_id || undefined);
        toast({ title: 'Comida agregada' });
      }
      resetMealForm();
      await fetchData();
      await refreshMealPlan(mealPlan.id);
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_meal', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar la comida.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteMeal = async (meal: NutritionMeal) => {
    if (!mealPlan) return;
    try {
      await nutritionApi.deleteMeal(meal.id, gym?.gym_id || undefined);
      toast({ title: 'Comida eliminada' });
      await fetchData();
      await refreshMealPlan(mealPlan.id);
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_meal', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar la comida.', variant: 'destructive' });
    }
  };

  // ---- Progress ----
  useEffect(() => {
    if (selectedClientId) {
      fetchProgress(selectedClientId);
      fetchClinical(selectedClientId);
    } else {
      setProgress([]);
      setClinical(null);
    }
  }, [selectedClientId, gym?.gym_id]);

  const fetchClinical = async (clientId: string) => {
    try {
      const res = await nutritionApi.getClinicalHistory(clientId, gym?.gym_id || undefined);
      const h = res.history;
      setClinical(h);
      setClinicalForm({
        allergies: h?.allergies || '',
        intolerances: h?.intolerances || '',
        pathologies: h?.pathologies || '',
        medications: h?.medications || '',
        supplements: h?.supplements || '',
        activity_level: h?.activity_level || 'moderate',
        goal: h?.goal || '',
        observations: h?.observations || '',
      });
    } catch (error) {
      reportError(error, { operation: 'nutrition_load_clinical', area: 'nutrition' });
    }
  };

  const saveClinical = async () => {
    if (!selectedClientId) return;
    setIsSavingClinical(true);
    try {
      const res = await nutritionApi.saveClinicalHistory(
        { client_id: selectedClientId, ...clinicalForm },
        gym?.gym_id || undefined
      );
      setClinical(res.history);
      toast({ title: 'Historial guardado' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_clinical', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar el historial clínico.', variant: 'destructive' });
    } finally {
      setIsSavingClinical(false);
    }
  };

  const fetchProgress = async (clientId: string) => {
    setIsLoadingProgress(true);
    try {
      const res = await nutritionApi.listProgress(clientId, gym?.gym_id || undefined);
      if (res.success) setProgress(res.progress || []);
    } catch (error) {
      reportError(error, { operation: 'nutrition_load_progress', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo cargar el progreso.', variant: 'destructive' });
    } finally {
      setIsLoadingProgress(false);
    }
  };

  const handleNutritionImport = async (
    kind: string,
    rows: Record<string, any>[],
    mode: 'create' | 'upsert'
  ): Promise<ImportResult | null> => {
    if (!gym?.gym_id) return null;

    try {
      const res = await nutritionApi.importProgressBatch(gym.gym_id, rows, mode);
      if (!res.success) {
        toast({
          title: 'Error al importar',
          description: res.error || 'No se pudo completar la importación.',
          variant: 'destructive',
        });
        return null;
      }

      const summary: ImportResult = {
        inserted: Number(res.inserted || 0),
        updated: Number(res.updated || 0),
        skipped: Number(res.skipped || 0),
        errors: Array.isArray(res.errors) ? res.errors : [],
      };

      if (selectedClientId) await fetchProgress(selectedClientId);
      return summary;
    } catch (error: any) {
      reportError(error, { operation: 'nutrition_import_batch', area: 'nutrition' });
      toast({
        title: 'Error al importar',
        description: error?.message || 'No se pudo completar la importación.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const selectedClient = activeClients.find((c) => c.client_id === selectedClientId);

  const baseForm = (client: any) => ({
    recorded_at: new Date().toISOString().slice(0, 10),
    sex: (client?.sex as Sex) || ('' as '' | Sex),
    age_years: client?.age != null ? String(client.age) : '',
    weight_kg: '',
    height_cm: '',
    sitting_height_cm: '',
    activity_level: 'moderate' as ActivityLevel,
    formula: 'jp7' as CompFormula,
    density_equation: 'siri' as DensityEquation,
    goal: 'maintenance' as EnergyGoal,
    body_fat_manual: '',
    muscle_kg: '',
    visceral_fat_manual: '',
    waist_cm: '',
    hip_cm: '',
    chest_cm: '',
    arm_cm: '',
    thigh_cm: '',
    adherence_score: '',
    notes: '',
    skinfolds: emptySites(SKINFOLD_SITES),
    perimeters: emptySites(PERIMETER_SITES),
    diameters: emptySites(DIAMETER_SITES),
  });

  const openNewProgress = () => {
    setEditingProgress(null);
    const last = progress[progress.length - 1];
    const form = baseForm(selectedClient);
    if (last) {
      form.weight_kg = last.weight_kg != null ? String(last.weight_kg) : '';
      form.height_cm = last.height_cm != null ? String(last.height_cm) : '';
      form.activity_level = (last.activity_level as ActivityLevel) || 'moderate';
      form.formula = (last.formula as CompFormula) || 'jp7';
      form.muscle_kg = last.muscle_kg != null ? String(last.muscle_kg) : '';
    }
    setProgressForm(form);
    setTargets({ target_calories: '', target_protein: '', target_carbs: '', target_fat: '' });
    setIsProgressDialogOpen(true);
  };

  const openEditProgress = (p: NutritionProgress) => {
    setEditingProgress(p);
    const toStr = (o: Record<string, number | null> | null | undefined) => {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o || {})) out[k] = v != null ? String(v) : '';
      return out;
    };
    setProgressForm({
      recorded_at: p.recorded_at || new Date().toISOString().slice(0, 10),
      sex: (p.sex as Sex) || '',
      age_years: p.age_years != null ? String(p.age_years) : '',
      weight_kg: p.weight_kg != null ? String(p.weight_kg) : '',
      height_cm: p.height_cm != null ? String(p.height_cm) : '',
      sitting_height_cm: p.sitting_height_cm != null ? String(p.sitting_height_cm) : '',
      activity_level: (p.activity_level as ActivityLevel) || 'moderate',
      formula: (p.formula as CompFormula) || 'jp7',
      density_equation: 'siri',
      goal: 'maintenance',
      body_fat_manual: p.body_fat_pct != null ? String(p.body_fat_pct) : '',
      muscle_kg: p.muscle_kg != null ? String(p.muscle_kg) : '',
      visceral_fat_manual:
        p.visceral_fat_source === 'manual' && p.visceral_fat_kg != null
          ? String(p.visceral_fat_kg)
          : '',
      waist_cm: p.waist_cm != null ? String(p.waist_cm) : '',
      hip_cm: p.hip_cm != null ? String(p.hip_cm) : '',
      chest_cm: p.chest_cm != null ? String(p.chest_cm) : '',
      arm_cm: p.arm_cm != null ? String(p.arm_cm) : '',
      thigh_cm: p.thigh_cm != null ? String(p.thigh_cm) : '',
      adherence_score: p.adherence_score != null ? String(p.adherence_score) : '',
      notes: p.notes || '',
      skinfolds: { ...emptySites(SKINFOLD_SITES), ...toStr(p.skinfolds) },
      perimeters: { ...emptySites(PERIMETER_SITES), ...toStr(p.perimeters) },
      diameters: { ...emptySites(DIAMETER_SITES), ...toStr(p.diameters) },
    });
    setTargets({
      target_calories: p.target_calories != null ? String(p.target_calories) : '',
      target_protein: p.target_protein != null ? String(p.target_protein) : '',
      target_carbs: p.target_carbs != null ? String(p.target_carbs) : '',
      target_fat: p.target_fat != null ? String(p.target_fat) : '',
    });
    setIsProgressDialogOpen(true);
  };

  const liveResults = useMemo(
    () =>
      computeProgressResults({
        sex: progressForm.sex as Sex,
        ageYears: num(progressForm.age_years),
        weightKg: num(progressForm.weight_kg),
        heightCm: num(progressForm.height_cm),
        waistCm: num(progressForm.waist_cm),
        hipCm: num(progressForm.hip_cm),
        formula: progressForm.formula,
        densityEquation: progressForm.density_equation,
        skinfolds: toNumRecord(progressForm.skinfolds),
        manualBodyFatPct: num(progressForm.body_fat_manual),
        activityLevel: progressForm.activity_level,
        goal: progressForm.goal,
        manualVisceralKg: num(progressForm.visceral_fat_manual),
      }),
    [progressForm]
  );

  // default targets from energy when fields are empty
  const effectiveTargets = useMemo(() => {
    const e = liveResults.energy;
    return {
      target_calories: targets.target_calories !== '' ? targets.target_calories : e.targetCalories != null ? String(e.targetCalories) : '',
      target_protein: targets.target_protein !== '' ? targets.target_protein : e.proteinG != null ? String(e.proteinG) : '',
      target_carbs: targets.target_carbs !== '' ? targets.target_carbs : e.carbsG != null ? String(e.carbsG) : '',
      target_fat: targets.target_fat !== '' ? targets.target_fat : e.fatG != null ? String(e.fatG) : '',
    };
  }, [targets, liveResults.energy]);

  const saveProgress = async () => {
    if (!selectedClientId) return;
    if (!progressForm.sex) {
      toast({ title: 'Sexo requerido', description: 'Indica el sexo del cliente para calcular la composición.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        client_id: selectedClientId,
        recorded_at: progressForm.recorded_at,
        sex: progressForm.sex,
        age_years: num(progressForm.age_years),
        weight_kg: num(progressForm.weight_kg),
        height_cm: num(progressForm.height_cm),
        sitting_height_cm: num(progressForm.sitting_height_cm),
        activity_level: progressForm.activity_level,
        formula: progressForm.formula,
        skinfolds: toNumRecord(progressForm.skinfolds),
        perimeters: toNumRecord(progressForm.perimeters),
        diameters: toNumRecord(progressForm.diameters),
        body_fat_pct: liveResults.bodyFat,
        fat_mass_kg: liveResults.fatMassKg,
        lean_mass_kg: liveResults.leanMassKg,
        body_density: liveResults.composition.bodyDensity,
        icc: liveResults.indices.icc,
        ict: liveResults.indices.ict,
        bmr: liveResults.energy.bmr,
        tdee: liveResults.energy.tdee,
        target_calories: num(effectiveTargets.target_calories),
        target_protein: num(effectiveTargets.target_protein),
        target_carbs: num(effectiveTargets.target_carbs),
        target_fat: num(effectiveTargets.target_fat),
        muscle_kg: num(progressForm.muscle_kg),
        metabolic_age: liveResults.metabolicAge.metabolicAge,
        visceral_fat_kg: liveResults.visceralFat.visceralFatKg,
        visceral_fat_index: liveResults.visceralFat.visceralFatIndex,
        visceral_fat_source: liveResults.visceralFat.source,
        muscle_mass_kg: liveResults.muscleMass.muscleMassKg,
        waist_cm: num(progressForm.waist_cm),
        hip_cm: num(progressForm.hip_cm),
        chest_cm: num(progressForm.chest_cm),
        arm_cm: num(progressForm.arm_cm),
        thigh_cm: num(progressForm.thigh_cm),
        adherence_score: num(progressForm.adherence_score),
        notes: progressForm.notes || null,
      };

      if (editingProgress) {
        await nutritionApi.updateProgress(editingProgress.id, { ...payload, client_id: undefined }, gym?.gym_id || undefined);
        toast({ title: 'Evaluación actualizada' });
      } else {
        await nutritionApi.createProgress(payload, gym?.gym_id || undefined);
        toast({ title: 'Evaluación registrada' });
      }

      // Persist sex on the client if it was empty
      if ((!selectedClient?.sex || selectedClient.sex !== progressForm.sex) && progressForm.sex) {
        try {
          await nutritionApi.setClientSex(selectedClientId, progressForm.sex, gym?.gym_id || undefined);
        } catch (e) {
          console.warn('No se pudo guardar el sexo en el cliente', e);
        }
      }

      setIsProgressDialogOpen(false);
      await fetchData();
      await fetchProgress(selectedClientId);
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_progress', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar la evaluación.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProgress = async (p: NutritionProgress) => {
    if (!window.confirm('¿Eliminar esta evaluación?')) return;
    try {
      await nutritionApi.deleteProgress(p.id, gym?.gym_id || undefined);
      toast({ title: 'Evaluación eliminada' });
      if (selectedClientId) await fetchProgress(selectedClientId);
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_progress', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar la evaluación.', variant: 'destructive' });
    }
  };

  const chartData = useMemo(
    () =>
      progress.map((p) => ({
        fecha: p.recorded_at ? new Date(p.recorded_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '',
        peso: p.weight_kg ?? null,
        magra: p.lean_mass_kg ?? null,
        grasa: p.body_fat_pct ?? null,
        imc: p.bmi ?? null,
        metabolica: p.metabolic_age ?? null,
        visceral: p.visceral_fat_kg ?? null,
        musculo: p.muscle_mass_kg ?? null,
      })),
    [progress]
  );

  const last = progress[progress.length - 1];
  const prev = progress[progress.length - 2];
  const delta = (a?: number | null, b?: number | null) =>
    a != null && b != null ? Math.round((a - b) * 100) / 100 : null;

  const upcoming = useMemo(
    () =>
      appointments
        .filter((a) => a.status === 'scheduled' && new Date(a.scheduled_at).getTime() >= Date.now())
        .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()),
    [appointments]
  );

  const dayAppointments = useMemo(() => {
    if (!selectedDate) return [];
    return appointments
      .filter((a) => {
        const d = new Date(a.scheduled_at);
        return (
          d.getFullYear() === selectedDate.getFullYear() &&
          d.getMonth() === selectedDate.getMonth() &&
          d.getDate() === selectedDate.getDate()
        );
      })
      .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  }, [appointments, selectedDate]);

  const openNewAppt = () => {
    setEditingAppt(null);
    setApptForm({ client_id: selectedClientId || '', scheduled_at: '', type: '', status: 'scheduled', notes: '' });
    setIsApptDialogOpen(true);
  };

  const openEditAppt = (a: NutritionAppointment) => {
    setEditingAppt(a);
    setApptForm({
      client_id: a.client_id,
      scheduled_at: a.scheduled_at ? a.scheduled_at.slice(0, 16) : '',
      type: a.type || '',
      status: a.status || 'scheduled',
      notes: a.notes || '',
    });
    setIsApptDialogOpen(true);
  };

  const saveAppt = async () => {
    if (!apptForm.client_id || !apptForm.scheduled_at) {
      toast({ title: 'Datos incompletos', description: 'Selecciona cliente y fecha/hora.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        client_id: apptForm.client_id,
        scheduled_at: new Date(apptForm.scheduled_at).toISOString(),
        type: apptForm.type || null,
        status: apptForm.status,
        notes: apptForm.notes || null,
      };
      if (editingAppt) {
        await nutritionApi.updateAppointment(editingAppt.id, payload, gym?.gym_id || undefined);
        toast({ title: 'Consulta actualizada' });
      } else {
        await nutritionApi.createAppointment(payload, gym?.gym_id || undefined);
        toast({ title: 'Consulta programada' });
      }
      setIsApptDialogOpen(false);
      const res = await nutritionApi.listAppointments(gym?.gym_id || undefined);
      if (res.success) setAppointments(res.appointments || []);
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_appointment', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar la consulta.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAppt = async (a: NutritionAppointment) => {
    if (!window.confirm('¿Eliminar esta consulta?')) return;
    try {
      await nutritionApi.deleteAppointment(a.id, gym?.gym_id || undefined);
      const res = await nutritionApi.listAppointments(gym?.gym_id || undefined);
      if (res.success) setAppointments(res.appointments || []);
      toast({ title: 'Consulta eliminada' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_appointment', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar la consulta.', variant: 'destructive' });
    }
  };

  // --- Agenda de reservas ---
  const loadAvailability = async () => {
    if (!gym?.gym_id) return;
    try {
      const [availRes, blocksRes] = await Promise.all([
        nutritionApi.listAvailability(gym.gym_id),
        nutritionApi.listAvailabilityBlocks(gym.gym_id),
      ]);
      if (availRes.success) setAvailability(availRes.availability || []);
      if (blocksRes.success) setAvailabilityBlocks(blocksRes.blocks || []);
    } catch (error) {
      reportError(error, { operation: 'nutrition_load_availability', area: 'nutrition' });
    }
  };

  const saveAvailability = async (row: Partial<NutritionAvailability>) => {
    if (!gym?.gym_id) return;
    setIsSaving(true);
    try {
      await nutritionApi.saveAvailability(row, gym.gym_id);
      await loadAvailability();
      toast({ title: 'Disponibilidad guardada' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_save_availability', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo guardar la disponibilidad.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const removeAvailability = async (id: string) => {
    if (!gym?.gym_id) return;
    setIsSaving(true);
    try {
      await nutritionApi.deleteAvailability(id, gym.gym_id);
      await loadAvailability();
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_availability', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar el horario.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const addAvailabilityBlock = async (blockedAt: string, reason: string | null) => {
    if (!gym?.gym_id) return;
    setIsSaving(true);
    try {
      await nutritionApi.createAvailabilityBlock(blockedAt, reason, gym.gym_id);
      await loadAvailability();
      toast({ title: 'Bloqueo agregado' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_add_block', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo agregar el bloqueo.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const removeAvailabilityBlock = async (id: string) => {
    if (!gym?.gym_id) return;
    setIsSaving(true);
    try {
      await nutritionApi.deleteAvailabilityBlock(id, gym.gym_id);
      await loadAvailability();
    } catch (error) {
      reportError(error, { operation: 'nutrition_delete_block', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo eliminar el bloqueo.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelAppt = async () => {
    if (!cancelTarget) return;
    setIsSaving(true);
    try {
      const res = await nutritionApi.cancelAppointment(
        cancelTarget.id,
        cancelReason.trim() || null,
        gym?.gym_id || undefined
      );
      if (!res.success) {
        toast({ title: 'Error', description: res.error || 'No se pudo cancelar la cita.', variant: 'destructive' });
        return;
      }
      const list = await nutritionApi.listAppointments(gym?.gym_id || undefined);
      if (list.success) setAppointments(list.appointments || []);
      setCancelTarget(null);
      setCancelReason('');
      toast({ title: 'Cita cancelada' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_cancel_appointment', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo cancelar la cita.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const checkInAppt = async (a: NutritionAppointment) => {
    try {
      const res = await nutritionApi.checkInAppointment(a.id);
      if (!res.success) {
        toast({ title: 'Error', description: (res as any).error || 'No se pudo marcar la llegada.', variant: 'destructive' });
        return;
      }
      const list = await nutritionApi.listAppointments(gym?.gym_id || undefined);
      if (list.success) setAppointments(list.appointments || []);
      toast({ title: 'Llegada registrada' });
    } catch (error) {
      reportError(error, { operation: 'nutrition_checkin_appointment', area: 'nutrition' });
      toast({ title: 'Error', description: 'No se pudo marcar la llegada.', variant: 'destructive' });
    }
  };

  const copyBookingLink = async () => {    if (!gym?.slug) {
      toast({ title: 'Error', description: 'El gimnasio no tiene slug público.', variant: 'destructive' });
      return;
    }
    const url = `${window.location.origin}/nutricion/${gym.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Enlace copiado', description: url });
    } catch {
      toast({ title: 'Enlace de reserva', description: url });
    }
  };

  const printHtml = (title: string, bodyHtml: string) => {
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      toast({ title: 'Ventana bloqueada', description: 'Permite las ventanas emergentes para imprimir.', variant: 'destructive' });
      return;
    }
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
        `<style>body{font-family:system-ui,Arial,sans-serif;padding:24px;color:#111}h1{font-size:20px;margin:0 0 4px}` +
        `h2{font-size:15px;margin:18px 0 6px;border-bottom:1px solid #ddd;padding-bottom:4px}` +
        `table{width:100%;border-collapse:collapse;margin-top:6px;font-size:13px}th,td{border:1px solid #ddd;padding:6px;text-align:left}` +
        `.muted{color:#666;font-size:12px}.grid{display:flex;flex-wrap:wrap;gap:12px}.box{border:1px solid #ddd;border-radius:8px;padding:8px 12px;min-width:120px}` +
        `.box b{display:block;font-size:16px}</style></head><body>${bodyHtml}` +
        `<script>window.onload=function(){window.print();}</script></body></html>`
    );
    w.document.close();
  };

  const printPlan = (plan: NutritionPlan) => {
    const rows = (plan.meals || []).map((m) => {
      const opts = (m.options || []).length
        ? `<div class="muted">Opciones: ${(m.options || [])
            .map((o) => `${escapeHtml(o.name)}${o.portion ? ` (${escapeHtml(o.portion)})` : ''}`)
            .join(' · ')}</div>`
        : '';
      return `<tr><td>${escapeHtml(mealTypeLabel(m.meal_type))}</td><td>${escapeHtml(m.name)}${m.percent_of_target != null ? ` <span class="muted">(${m.percent_of_target}%)</span>` : ''}<br><span class="muted">${escapeHtml(m.description || '')}</span>${opts}</td><td>${m.calories ?? '—'} kcal<br><span class="muted">P ${m.protein ?? '—'} / C ${m.carbs ?? '—'} / G ${m.fat ?? '—'}</span></td></tr>`;
    }).join('');
    const body = `
      <h1>Plan nutricional: ${escapeHtml(plan.name)}</h1>
      <div class="muted">${escapeHtml(plan.client_name || 'Plantilla general')}${plan.objective ? ` · Objetivo: ${escapeHtml(plan.objective)}` : ''}</div>
      <div class="grid" style="margin-top:12px">
        <div class="box muted">Calorías<b>${plan.target_calories ?? '—'} kcal</b></div>
        <div class="box muted">Proteína<b>${plan.target_protein ?? '—'} g</b></div>
        <div class="box muted">Carbohidratos<b>${plan.target_carbs ?? '—'} g</b></div>
        <div class="box muted">Grasas<b>${plan.target_fat ?? '—'} g</b></div>
      </div>
      <h2>Comidas</h2>
      <table><thead><tr><th>Tiempo</th><th>Comida / opciones</th><th>Nutrientes</th></tr></thead><tbody>${rows || '<tr><td colspan="3">Sin comidas</td></tr>'}</tbody></table>
      ${plan.notes ? `<h2>Notas</h2><p>${escapeHtml(plan.notes)}</p>` : ''}
    `;
    printHtml(`Plan - ${plan.name}`, body);
  };

  const printEvaluation = (p: NutritionProgress) => {
    const body = `
      <h1>Evaluación antropométrica</h1>
      <div class="muted">${escapeHtml(p.client_name || selectedClient?.name || '')} · ${escapeHtml(p.recorded_at)}</div>
      <div class="grid" style="margin-top:12px">
        <div class="box muted">Peso<b>${fmt(p.weight_kg, ' kg')}</b></div>
        <div class="box muted">% Grasa<b>${fmt(p.body_fat_pct, '%')}</b></div>
        <div class="box muted">Masa magra<b>${fmt(p.lean_mass_kg, ' kg')}</b></div>
        <div class="box muted">IMC<b>${fmt(p.bmi)}</b></div>
        <div class="box muted">ICC<b>${fmt(p.icc)}</b></div>
        <div class="box muted">ICT<b>${fmt(p.ict)}</b></div>
      </div>
      <h2>Energía</h2>
      <table><tbody>
        <tr><td>TMB</td><td>${fmt(p.bmr, ' kcal')}</td></tr>
        <tr><td>GET/TDEE</td><td>${fmt(p.tdee, ' kcal')}</td></tr>
        <tr><td>Objetivo</td><td>${fmt(p.target_calories, ' kcal')}</td></tr>
        <tr><td>Macros (P/C/G)</td><td>${fmt(p.target_protein)} / ${fmt(p.target_carbs)} / ${fmt(p.target_fat)} g</td></tr>
      </tbody></table>
      <h2>Métricas metabólicas</h2>
      <table><tbody>
        <tr><td>Edad metabólica</td><td>${fmt(p.metabolic_age, ' años')}</td></tr>
        <tr><td>Grasa visceral</td><td>${fmt(p.visceral_fat_kg, ' kg')}${p.visceral_fat_source === 'manual' ? ' (manual)' : ' (estimada)'}</td></tr>
        <tr><td>Índice de grasa visceral</td><td>${fmt(p.visceral_fat_index, ' / 20')}</td></tr>
        <tr><td>Masa muscular</td><td>${fmt(p.muscle_mass_kg, ' kg')}</td></tr>
      </tbody></table>
      ${p.notes ? `<h2>Notas</h2><p>${escapeHtml(p.notes)}</p>` : ''}
    `;
    printHtml('Evaluación', body);
  };

  const renderSiteGrid = (
    sites: SiteDef[],
    values: Record<string, string>,
    onChange: (key: string, value: string) => void,
    unit: string
  ) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {sites.map((s) => (
        <div className="space-y-1" key={s.key}>
          <Label className="text-xs">
            {s.label} <span className="text-muted-foreground">({unit})</span>
          </Label>
          <Input
            type="number"
            value={values[s.key] ?? ''}
            onChange={(e) => onChange(s.key, e.target.value)}
            disabled={isSaving}
          />
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <DashboardLayout title="Nutrición">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Nutrición"
      subtitle={gym ? `Control nutricional de ${gym.name}` : 'Control nutricional global'}
      actions={
        activeTab === 'planes' && canManagePlans ? (
          <Button onClick={openNewPlan} variant="gradient" disabled={!gym}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Plan
          </Button>
        ) : activeTab === 'progreso' && canManageProgress && selectedClientId ? (
          <Button onClick={openNewProgress} variant="gradient" disabled={!gym}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Evaluación
          </Button>
        ) : undefined
      }
    >
      {isSuperAdmin && !gym && <NoGymSelected />}

      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ClipboardList className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Planes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Apple className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.templates}</p>
                  <p className="text-xs text-muted-foreground">Plantillas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.assigned}</p>
                  <p className="text-xs text-muted-foreground">Asignados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Utensils className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.meals}</p>
                  <p className="text-xs text-muted-foreground">Comidas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="planes">
              <ClipboardList className="h-4 w-4 mr-2" /> Planes
            </TabsTrigger>
            <TabsTrigger value="progreso">
              <LineChartIcon className="h-4 w-4 mr-2" /> Evaluaciones
            </TabsTrigger>
            <TabsTrigger value="clinico">
              <Stethoscope className="h-4 w-4 mr-2" /> Clínico
            </TabsTrigger>
            <TabsTrigger value="agenda">
              <Calendar className="h-4 w-4 mr-2" /> Agenda
            </TabsTrigger>
            {canManageProgress && (
              <TabsTrigger value="importar">
                <Upload className="h-4 w-4 mr-2" /> Importar
              </TabsTrigger>
            )}
          </TabsList>

          {/* ---- PLANES ---- */}
          <TabsContent value="planes" className="space-y-4 mt-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <Card key={plan.id} className="glass-card">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{plan.client_name || 'Plantilla general'}</p>
                      </div>
                      <Badge variant={plan.client_id ? 'info' : 'secondary'}>
                        {plan.client_id ? 'Asignado' : 'Plantilla'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {plan.objective && <p className="text-sm text-muted-foreground">Objetivo: {plan.objective}</p>}
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {plan.target_calories != null && <span>{plan.target_calories} kcal</span>}
                      {plan.target_protein != null && <span>P {plan.target_protein}g</span>}
                      {plan.target_carbs != null && <span>C {plan.target_carbs}g</span>}
                      {plan.target_fat != null && <span>G {plan.target_fat}g</span>}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Utensils className="h-4 w-4" />
                      {plan.meals?.length || 0} comida{(plan.meals?.length || 0) !== 1 ? 's' : ''}
                    </div>
                    {(() => {
                      const pct = (plan.meals || []).reduce((s, m) => s + (m.percent_of_target || 0), 0);
                      if (!pct) return null;
                      return (
                        <p className={cn('text-xs', pct > 100 ? 'text-destructive' : 'text-muted-foreground')}>
                          Distribución: {Math.round(pct)}% del objetivo
                        </p>
                      );
                    })()}
                    {isSuperAdmin && !gym && (
                      <div className="flex items-center gap-2 pt-1 text-muted-foreground border-t border-border/10">
                        <Building2 className="h-3 w-3" />
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          ID: {plan.gym_id?.substring(0, 8)}...
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-border/10">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => openMeals(plan)}>
                          <Utensils className="h-3.5 w-3.5 mr-1.5" />
                          Comidas
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => printPlan(plan)} title="Imprimir plan">
                          <Printer className="h-4 w-4" />
                        </Button>
                      </div>
                      {canManagePlans && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted" onClick={() => openEditPlan(plan)} title="Editar plan">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-destructive/10 text-destructive" onClick={() => deletePlan(plan)} title="Eliminar plan">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {plans.length === 0 && (
                <Card className="glass-card col-span-full">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    No hay planes de nutrición todavía.
                    {canManagePlans && gym && ' Crea el primero con "Nuevo Plan".'}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ---- EVALUACIONES ---- */}
          <TabsContent value="progreso" className="space-y-4 mt-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="max-w-sm space-y-2">
                  <Label>Cliente</Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {activeClients.length === 0 && (
                        <div className="p-4 text-sm text-center text-muted-foreground">No hay clientes activos.</div>
                      )}
                      {activeClients
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {!selectedClientId && (
              <Card className="glass-card">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Scale className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  Selecciona un cliente para ver y registrar su evaluación.
                </CardContent>
              </Card>
            )}

            {selectedClientId && isLoadingProgress && (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {selectedClientId && !isLoadingProgress && (
              <>
                {last && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
                    <Card className="glass-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Scale className="h-4 w-4 text-primary" /> Composición
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Peso</span><span>{fmt(last.weight_kg, ' kg')} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.weight_kg, prev.weight_kg), ' kg')})</span>}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">% Grasa</span><span>{fmt(last.body_fat_pct, '%')} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.body_fat_pct, prev.body_fat_pct), ' pt')})</span>}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Masa grasa</span><span>{fmt(last.fat_mass_kg, ' kg')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Masa magra</span><span>{fmt(last.lean_mass_kg, ' kg')} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.lean_mass_kg, prev.lean_mass_kg), ' kg')})</span>}</span></div>
                      </CardContent>
                    </Card>

                    <Card className="glass-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Ruler className="h-4 w-4 text-primary" /> Índices
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">IMC</span><span>{fmt(last.bmi)} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.bmi, prev.bmi))})</span>}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">ICC (cintura/cadera)</span><span>{fmt(last.icc)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">ICT (cintura/talla)</span><span>{fmt(last.ict)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Adherencia</span><span>{fmt(last.adherence_score, '%')}</span></div>
                      </CardContent>
                    </Card>

                    <Card className="glass-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calculator className="h-4 w-4 text-primary" /> Energía
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">TMB</span><span>{fmt(last.bmr, ' kcal')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">GET/TDEE</span><span>{fmt(last.tdee, ' kcal')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Objetivo</span><span>{fmt(last.target_calories, ' kcal')}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-muted-foreground">Macros (P/C/G)</span><span>{fmt(last.target_protein)} / {fmt(last.target_carbs)} / {fmt(last.target_fat)} g</span></div>
                      </CardContent>
                    </Card>

                    <Card className="glass-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <HeartPulse className="h-4 w-4 text-primary" /> Metabólicas
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Edad metabólica</span><span>{fmt(last.metabolic_age, ' años')} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.metabolic_age, prev.metabolic_age), ' años')})</span>}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Grasa visceral</span><span>{fmt(last.visceral_fat_kg, ' kg')} {last.visceral_fat_source === 'manual' && <span className="text-xs text-muted-foreground">(manual)</span>}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Índice visceral</span><span>{fmt(last.visceral_fat_index, ' / 20')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Masa muscular</span><span>{fmt(last.muscle_mass_kg, ' kg')} {prev && <span className="text-xs text-muted-foreground">({fmtDelta(delta(last.muscle_mass_kg, prev.muscle_mass_kg), ' kg')})</span>}</span></div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {last && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card className="glass-card">
                      <CardHeader><CardTitle className="text-base">Peso y masa magra</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="fecha" fontSize={12} />
                            <YAxis fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="peso" name="Peso (kg)" stroke="hsl(var(--primary))" connectNulls />
                            <Line type="monotone" dataKey="magra" name="Masa magra (kg)" stroke="#6366f1" connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card className="glass-card">
                      <CardHeader><CardTitle className="text-base">% Grasa e IMC</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="fecha" fontSize={12} />
                            <YAxis yAxisId="left" fontSize={12} />
                            <YAxis yAxisId="right" orientation="right" fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="grasa" name="% Grasa" stroke="#22c55e" connectNulls />
                            <Line yAxisId="right" type="monotone" dataKey="imc" name="IMC" stroke="#f59e0b" connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card className="glass-card">
                      <CardHeader><CardTitle className="text-base">Edad metabólica y grasa visceral</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="fecha" fontSize={12} />
                            <YAxis yAxisId="left" fontSize={12} />
                            <YAxis yAxisId="right" orientation="right" fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="metabolica" name="Edad metabólica" stroke="#ec4899" connectNulls />
                            <Line yAxisId="right" type="monotone" dataKey="visceral" name="Grasa visceral (kg)" stroke="#8b5cf6" connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    <Card className="glass-card">
                      <CardHeader><CardTitle className="text-base">Masa muscular</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="fecha" fontSize={12} />
                            <YAxis fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="musculo" name="Masa muscular (kg)" stroke="#0ea5e9" connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <Card className="glass-card">
                  <CardContent className="p-0">
                    {progress.length === 0 ? (
                      <div className="py-12 text-center text-muted-foreground">
                        Sin evaluaciones registradas.
                        {canManageProgress && ' Usa "Nueva Evaluación".'}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Fecha</TableHead>
                              <TableHead>Peso</TableHead>
                              <TableHead>% Grasa</TableHead>
                              <TableHead>Masa magra</TableHead>
                              <TableHead>IMC</TableHead>
                              <TableHead>ICC</TableHead>
                              <TableHead>Adherencia</TableHead>
                              <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {progress.slice().reverse().map((p) => (
                              <TableRow key={p.id}>
                                <TableCell>{p.recorded_at}</TableCell>
                                <TableCell>{fmt(p.weight_kg, ' kg')}</TableCell>
                                <TableCell>{fmt(p.body_fat_pct, '%')}</TableCell>
                                <TableCell>{fmt(p.lean_mass_kg, ' kg')}</TableCell>
                                <TableCell>{fmt(p.bmi)}</TableCell>
                                <TableCell>{fmt(p.icc)}</TableCell>
                                <TableCell>{fmt(p.adherence_score, '%')}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => printEvaluation(p)} title="Imprimir evaluación">
                                      <Printer className="h-4 w-4" />
                                    </Button>
                                    {canManageProgress && (
                                      <>
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditProgress(p)}>
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => deleteProgress(p)}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ---- CLÍNICO ---- */}
          <TabsContent value="clinico" className="space-y-4 mt-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="max-w-sm space-y-2">
                  <Label>Cliente</Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                    <SelectContent>
                      {activeClients.length === 0 && (
                        <div className="p-4 text-sm text-center text-muted-foreground">No hay clientes activos.</div>
                      )}
                      {activeClients.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {!selectedClientId ? (
              <Card className="glass-card">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Stethoscope className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  Selecciona un cliente para ver y editar su historial clínico.
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-primary" /> Historial clínico de {selectedClient?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1"><Label className="text-xs">Alergias</Label><Textarea rows={2} value={clinicalForm.allergies} onChange={(e) => setClinicalForm((c) => ({ ...c, allergies: e.target.value }))} disabled={isSavingClinical} placeholder="Ej: lactosa, frutos secos" /></div>
                    <div className="space-y-1"><Label className="text-xs">Intolerancias</Label><Textarea rows={2} value={clinicalForm.intolerances} onChange={(e) => setClinicalForm((c) => ({ ...c, intolerances: e.target.value }))} disabled={isSavingClinical} /></div>
                    <div className="space-y-1"><Label className="text-xs">Patologías</Label><Textarea rows={2} value={clinicalForm.pathologies} onChange={(e) => setClinicalForm((c) => ({ ...c, pathologies: e.target.value }))} disabled={isSavingClinical} /></div>
                    <div className="space-y-1"><Label className="text-xs">Medicamentos</Label><Textarea rows={2} value={clinicalForm.medications} onChange={(e) => setClinicalForm((c) => ({ ...c, medications: e.target.value }))} disabled={isSavingClinical} /></div>
                    <div className="space-y-1"><Label className="text-xs">Suplementos</Label><Textarea rows={2} value={clinicalForm.supplements} onChange={(e) => setClinicalForm((c) => ({ ...c, supplements: e.target.value }))} disabled={isSavingClinical} /></div>
                    <div className="space-y-1">
                      <Label className="text-xs">Nivel de actividad por defecto</Label>
                      <Select value={clinicalForm.activity_level} onValueChange={(v) => setClinicalForm((c) => ({ ...c, activity_level: v }))} disabled={isSavingClinical}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(ACTIVITY_LABELS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Objetivo por defecto</Label><Input value={clinicalForm.goal} onChange={(e) => setClinicalForm((c) => ({ ...c, goal: e.target.value }))} placeholder="Ej: Pérdida de grasa" disabled={isSavingClinical} /></div>
                  <div className="space-y-1"><Label className="text-xs">Observaciones</Label><Textarea rows={3} value={clinicalForm.observations} onChange={(e) => setClinicalForm((c) => ({ ...c, observations: e.target.value }))} disabled={isSavingClinical} /></div>
                  <div className="flex justify-end">
                    <Button onClick={saveClinical} variant="gradient" disabled={isSavingClinical || !canManageProgress}>
                      {isSavingClinical ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Guardar historial
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ---- AGENDA ---- */}
          <TabsContent value="agenda" className="space-y-4 mt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Próximas consultas: {upcoming.length}
              </p>
              {canManageProgress && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsAvailabilityOpen(true)} disabled={!gym}>
                    <Clock className="mr-2 h-4 w-4" /> Disponibilidad
                  </Button>
                  <Button variant="outline" size="sm" onClick={copyBookingLink} disabled={!gym}>
                    <Link2 className="mr-2 h-4 w-4" /> Compartir enlace
                  </Button>
                  <Button variant="gradient" onClick={openNewAppt} disabled={!gym}>
                    <Plus className="h-4 w-4 mr-2" /> Nueva Consulta
                  </Button>
                </div>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Calendario
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <NutritionMonthCalendar
                    appointments={appointments}
                    selectedDate={selectedDate}
                    onSelectDate={setSelectedDate}
                  />
                </CardContent>
              </Card>

              <Card className="glass-card lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    {selectedDate
                      ? selectedDate.toLocaleDateString('es-ES', { dateStyle: 'full' })
                      : 'Citas del día'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dayAppointments.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      No hay citas para este día.
                    </p>
                  ) : (
                    dayAppointments.map((a) => (
                      <div
                        key={a.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {new Date(a.scheduled_at).toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {' · '}
                            {a.client_name || a.contact_name || 'Cliente'}
                            {a.type ? ` (${a.type})` : ''}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {a.source === 'public' ? 'Reservada por la clienta' : 'Creada por el equipo'}
                            {a.cancel_reason ? ` · ${a.cancel_reason}` : ''}
                          </p>
                        </div>
                        <Badge variant={a.status === 'scheduled' ? 'info' : a.status === 'completed' ? 'success' : 'secondary'}>
                          {APPT_STATUS[a.status] || a.status}
                        </Badge>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="glass-card">
              <CardContent className="p-0">
                {appointments.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">Sin consultas programadas.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha y hora</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Origen</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.slice().sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()).map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>{new Date(a.scheduled_at).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}</TableCell>
                            <TableCell>{a.client_name || a.contact_name || '—'}</TableCell>
                            <TableCell>{a.type || '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {a.source === 'public' ? 'Pública' : 'Equipo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={a.status === 'scheduled' ? 'info' : a.status === 'completed' ? 'success' : 'secondary'}>
                                {APPT_STATUS[a.status] || a.status}
                              </Badge>
                              {a.cancel_reason && (
                                <p className="mt-1 text-xs text-muted-foreground">{a.cancel_reason}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {canManageProgress && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => openEditAppt(a)}
                                    title="Editar"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  {a.status === 'scheduled' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-green-600 hover:bg-green-600/10"
                                      onClick={() => checkInAppt(a)}
                                      title="Marcar llegada"
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {a.status !== 'cancelled' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => {
                                        setCancelTarget(a);
                                        setCancelReason('');
                                      }}
                                      title="Cancelar cita"
                                    >
                                      <CalendarX className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => deleteAppt(a)} title="Eliminar">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {canManageProgress && (
            <TabsContent value="importar" className="mt-4">
              <ExcelImportWizard
                kinds={nutritionImportKinds}
                defaultKind="nutrition_progress"
                title="Importar evaluaciones de nutrición"
                description="Sube un Excel con las métricas de tus pacientes y el sistema calcula la composición, energía y metas."
                notice={
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                    Las clientas deben existir antes de importar sus evaluaciones. Si tu archivo trae
                    pacientes nuevas, impórtalas primero desde <span className="font-medium">Clientes → botón Importar</span>.
                  </div>
                }
                onImport={handleNutritionImport}
                onComplete={async () => {
                  await fetchData();
                  if (selectedClientId) await fetchProgress(selectedClientId);
                }}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* ---- PLAN DIALOG ---- */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Editar Plan' : 'Nuevo Plan de Nutrición'}</DialogTitle>
            <DialogDescription>Define el plan y sus metas. Déjalo sin cliente para usarlo como plantilla.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={planForm.name} onChange={(e) => setPlanForm((p) => ({ ...p, name: e.target.value }))} placeholder="Ej: Definición 4 semanas" disabled={isSaving} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={planForm.client_id || 'none'} onValueChange={(v) => setPlanForm((p) => ({ ...p, client_id: v === 'none' ? '' : v }))} disabled={isSaving}>
                  <SelectTrigger><SelectValue placeholder="Plantilla (sin cliente)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Plantilla (sin cliente)</SelectItem>
                    {activeClients.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                      <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Objetivo</Label>
                <Input value={planForm.objective} onChange={(e) => setPlanForm((p) => ({ ...p, objective: e.target.value }))} placeholder="Ej: Pérdida de grasa" disabled={isSaving} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2"><Label className="text-xs">Calorías</Label><Input type="number" value={planForm.target_calories} onChange={(e) => setPlanForm((p) => ({ ...p, target_calories: e.target.value }))} disabled={isSaving} /></div>
              <div className="space-y-2"><Label className="text-xs">Proteína (g)</Label><Input type="number" value={planForm.target_protein} onChange={(e) => setPlanForm((p) => ({ ...p, target_protein: e.target.value }))} disabled={isSaving} /></div>
              <div className="space-y-2"><Label className="text-xs">Carbs (g)</Label><Input type="number" value={planForm.target_carbs} onChange={(e) => setPlanForm((p) => ({ ...p, target_carbs: e.target.value }))} disabled={isSaving} /></div>
              <div className="space-y-2"><Label className="text-xs">Grasas (g)</Label><Input type="number" value={planForm.target_fat} onChange={(e) => setPlanForm((p) => ({ ...p, target_fat: e.target.value }))} disabled={isSaving} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Inicio</Label><Input type="date" value={planForm.start_date} onChange={(e) => setPlanForm((p) => ({ ...p, start_date: e.target.value }))} disabled={isSaving} /></div>
              <div className="space-y-2"><Label>Fin</Label><Input type="date" value={planForm.end_date} onChange={(e) => setPlanForm((p) => ({ ...p, end_date: e.target.value }))} disabled={isSaving} /></div>
            </div>
            <div className="space-y-2"><Label>Descripción</Label><Textarea value={planForm.description} onChange={(e) => setPlanForm((p) => ({ ...p, description: e.target.value }))} rows={2} disabled={isSaving} /></div>
            <div className="space-y-2"><Label>Notas</Label><Textarea value={planForm.notes} onChange={(e) => setPlanForm((p) => ({ ...p, notes: e.target.value }))} rows={2} disabled={isSaving} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPlanDialogOpen(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={savePlan} variant="gradient" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- MEALS DIALOG ---- */}
      <Dialog open={isMealsDialogOpen} onOpenChange={setIsMealsDialogOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Utensils className="h-5 w-5" /> Comidas: {mealPlan?.name}</DialogTitle>
            <DialogDescription>{mealPlan?.client_name || 'Plantilla general'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              {(mealPlan?.meals?.length || 0) === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Sin comidas aún.</div>}
              {mealPlan?.meals?.map((meal) => (
                <div key={meal.id} className={cn('flex items-center justify-between rounded-lg border border-border/60 p-3', editingMeal?.id === meal.id && 'ring-2 ring-primary/50')}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm"><span className="text-muted-foreground">{mealTypeLabel(meal.meal_type)} · </span>{meal.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {meal.calories != null ? `${meal.calories} kcal` : ''}
                      {meal.protein != null ? ` · P ${meal.protein}g` : ''}
                      {meal.carbs != null ? ` · C ${meal.carbs}g` : ''}
                      {meal.fat != null ? ` · G ${meal.fat}g` : ''}
                    </p>
                  </div>
                  {canManagePlans && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startMeal(meal)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => deleteMeal(meal)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {canManagePlans && (
              <div className="rounded-lg border border-dashed border-border/60 p-3 space-y-3">
                <p className="text-sm font-semibold">{editingMeal ? 'Editar comida' : 'Agregar comida'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Tipo</Label>
                    <Select value={mealForm.meal_type} onValueChange={(v) => setMealForm((p) => ({ ...p, meal_type: v as NutritionMealType }))} disabled={isSaving}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{MEAL_TYPES.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label className="text-xs">Nombre *</Label><Input value={mealForm.name} onChange={(e) => setMealForm((p) => ({ ...p, name: e.target.value }))} placeholder="Ej: Avena con fruta" disabled={isSaving} /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2"><Label className="text-xs">Calorías</Label><Input type="number" value={mealForm.calories} onChange={(e) => setMealForm((p) => ({ ...p, calories: e.target.value }))} disabled={isSaving} /></div>
                  <div className="space-y-2"><Label className="text-xs">Proteína</Label><Input type="number" value={mealForm.protein} onChange={(e) => setMealForm((p) => ({ ...p, protein: e.target.value }))} disabled={isSaving} /></div>
                  <div className="space-y-2"><Label className="text-xs">Carbs</Label><Input type="number" value={mealForm.carbs} onChange={(e) => setMealForm((p) => ({ ...p, carbs: e.target.value }))} disabled={isSaving} /></div>
                  <div className="space-y-2"><Label className="text-xs">Grasas</Label><Input type="number" value={mealForm.fat} onChange={(e) => setMealForm((p) => ({ ...p, fat: e.target.value }))} disabled={isSaving} /></div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">% del objetivo diario</Label>
                  <Input type="number" value={mealForm.percent_of_target} onChange={(e) => setMealForm((p) => ({ ...p, percent_of_target: e.target.value }))} className="max-w-[140px]" disabled={isSaving} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Opciones / intercambios</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setMealForm((p) => ({ ...p, options: [...p.options, emptyMealOption()] }))} disabled={isSaving}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
                    </Button>
                  </div>
                  {mealForm.options.length === 0 && <p className="text-xs text-muted-foreground">Sin opciones.</p>}
                  {mealForm.options.map((o, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end rounded-md border border-border/50 p-2">
                      <div className="col-span-4 space-y-1">
                        <Label className="text-[10px]">Alimento</Label>
                        <Input value={o.name} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, name: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[10px]">Porción</Label>
                        <Input value={o.portion} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, portion: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <Label className="text-[10px]">kcal</Label>
                        <Input type="number" value={o.kcal} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, kcal: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <Label className="text-[10px]">P</Label>
                        <Input type="number" value={o.protein} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, protein: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <Label className="text-[10px]">C</Label>
                        <Input type="number" value={o.carbs} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, carbs: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <Label className="text-[10px]">G</Label>
                        <Input type="number" value={o.fat} onChange={(e) => setMealForm((p) => ({ ...p, options: p.options.map((op, i) => (i === idx ? { ...op, fat: e.target.value } : op)) }))} disabled={isSaving} />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => setMealForm((p) => ({ ...p, options: p.options.filter((_, i) => i !== idx) }))} disabled={isSaving}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2"><Label className="text-xs">Descripción</Label><Input value={mealForm.description} onChange={(e) => setMealForm((p) => ({ ...p, description: e.target.value }))} placeholder="Opcional" disabled={isSaving} /></div>
                <div className="flex justify-end gap-2">
                  {editingMeal && <Button variant="outline" size="sm" onClick={resetMealForm} disabled={isSaving}>Cancelar edición</Button>}
                  <Button size="sm" variant="gradient" onClick={saveMeal} disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    {editingMeal ? 'Guardar' : 'Agregar'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMealsDialogOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- EVALUATION DIALOG ---- */}
      <Dialog open={isProgressDialogOpen} onOpenChange={setIsProgressDialogOpen}>
        <DialogContent className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProgress ? 'Editar Evaluación' : 'Nueva Evaluación Antropométrica'}</DialogTitle>
            <DialogDescription>{selectedClient?.name}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Section title="Datos básicos" icon={<Scale className="h-4 w-4 text-primary" />} defaultOpen>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={progressForm.recorded_at} onChange={(e) => setProgressForm((p) => ({ ...p, recorded_at: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Sexo *</Label>
                  <Select value={progressForm.sex || 'none'} onValueChange={(v) => setProgressForm((p) => ({ ...p, sex: v === 'none' ? '' : (v as Sex) }))} disabled={isSaving}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {SEX_OPTIONS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Edad (años)</Label><Input type="number" value={progressForm.age_years} onChange={(e) => setProgressForm((p) => ({ ...p, age_years: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Peso (kg)</Label><Input type="number" value={progressForm.weight_kg} onChange={(e) => setProgressForm((p) => ({ ...p, weight_kg: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Talla (cm)</Label><Input type="number" value={progressForm.height_cm} onChange={(e) => setProgressForm((p) => ({ ...p, height_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Talla sentado (cm)</Label><Input type="number" value={progressForm.sitting_height_cm} onChange={(e) => setProgressForm((p) => ({ ...p, sitting_height_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Cintura (cm)</Label><Input type="number" value={progressForm.waist_cm} onChange={(e) => setProgressForm((p) => ({ ...p, waist_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Cadera (cm)</Label><Input type="number" value={progressForm.hip_cm} onChange={(e) => setProgressForm((p) => ({ ...p, hip_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Pecho (cm)</Label><Input type="number" value={progressForm.chest_cm} onChange={(e) => setProgressForm((p) => ({ ...p, chest_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Brazo (cm)</Label><Input type="number" value={progressForm.arm_cm} onChange={(e) => setProgressForm((p) => ({ ...p, arm_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Muslo (cm)</Label><Input type="number" value={progressForm.thigh_cm} onChange={(e) => setProgressForm((p) => ({ ...p, thigh_cm: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Músculo (kg)</Label><Input type="number" value={progressForm.muscle_kg} onChange={(e) => setProgressForm((p) => ({ ...p, muscle_kg: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Grasa visceral manual (kg)</Label><Input type="number" value={progressForm.visceral_fat_manual} onChange={(e) => setProgressForm((p) => ({ ...p, visceral_fat_manual: e.target.value }))} disabled={isSaving} placeholder={liveResults.visceralFat.visceralFatKg != null ? String(liveResults.visceralFat.visceralFatKg) : 'auto'} /></div>
              </div>
            </Section>

            <Section title="Pliegues cutáneos" icon={<Ruler className="h-4 w-4 text-primary" />}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-2">
                <div className="space-y-1">
                  <Label className="text-xs">Método</Label>
                  <Select value={progressForm.formula} onValueChange={(v) => setProgressForm((p) => ({ ...p, formula: v as CompFormula }))} disabled={isSaving}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(FORMULA_LABELS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ecuación</Label>
                  <Select value={progressForm.density_equation} onValueChange={(v) => setProgressForm((p) => ({ ...p, density_equation: v as DensityEquation }))} disabled={isSaving}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DENSITY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              {renderSiteGrid(SKINFOLD_SITES, progressForm.skinfolds, (k, v) => setProgressForm((p) => ({ ...p, skinfolds: { ...p.skinfolds, [k]: v } })), 'mm')}
              <div className="space-y-1 pt-1">
                <Label className="text-xs">% de grasa manual (si no usás pliegues)</Label>
                <Input type="number" value={progressForm.body_fat_manual} onChange={(e) => setProgressForm((p) => ({ ...p, body_fat_manual: e.target.value }))} disabled={isSaving} className="max-w-[160px]" />
              </div>
            </Section>

            <Section title="Perímetros (cm)" icon={<Ruler className="h-4 w-4 text-primary" />}>
              {renderSiteGrid(PERIMETER_SITES, progressForm.perimeters, (k, v) => setProgressForm((p) => ({ ...p, perimeters: { ...p.perimeters, [k]: v } })), 'cm')}
            </Section>

            <Section title="Diámetros óseos (cm)" icon={<Ruler className="h-4 w-4 text-primary" />}>
              {renderSiteGrid(DIAMETER_SITES, progressForm.diameters, (k, v) => setProgressForm((p) => ({ ...p, diameters: { ...p.diameters, [k]: v } })), 'cm')}
            </Section>

            <Section title="Resultados" icon={<Activity className="h-4 w-4 text-primary" />} defaultOpen>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">IMC</p><p className="text-lg font-bold">{fmt(liveResults.indices.bmi)}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">% Grasa</p><p className="text-lg font-bold">{fmt(liveResults.bodyFat, '%')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Masa grasa</p><p className="text-lg font-bold">{fmt(liveResults.fatMassKg, ' kg')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Masa magra</p><p className="text-lg font-bold">{fmt(liveResults.leanMassKg, ' kg')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">ICC</p><p className="text-lg font-bold">{fmt(liveResults.indices.icc)}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">ICT</p><p className="text-lg font-bold">{fmt(liveResults.indices.ict)}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Densidad</p><p className="text-lg font-bold">{fmt(liveResults.composition.bodyDensity)}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Método</p><p className="text-sm font-semibold pt-1">{FORMULA_LABELS[progressForm.formula]}</p></div>
              </div>
              {!liveResults.composition.ok && liveResults.composition.missing.length > 0 && (
                <p className="text-xs text-amber-600">
                  Faltan pliegues para el método: {liveResults.composition.missing.join(', ')}.
                </p>
              )}
              {liveResults.composition.note && <p className="text-xs text-muted-foreground">{liveResults.composition.note}</p>}
            </Section>

            <Section title="Métricas metabólicas" icon={<HeartPulse className="h-4 w-4 text-primary" />}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Edad metabólica</p><p className="text-lg font-bold">{fmt(liveResults.metabolicAge.metabolicAge, ' años')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Grasa visceral</p><p className="text-lg font-bold">{fmt(liveResults.visceralFat.visceralFatKg, ' kg')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Índice visceral</p><p className="text-lg font-bold">{fmt(liveResults.visceralFat.visceralFatIndex, ' / 20')}</p></div>
                <div className="rounded-lg bg-muted/40 p-2"><p className="text-xs text-muted-foreground">Masa muscular</p><p className="text-lg font-bold">{fmt(liveResults.muscleMass.muscleMassKg, ' kg')}</p></div>
              </div>
              <p className="text-xs text-muted-foreground">
                {liveResults.metabolicAge.ok && liveResults.metabolicAge.differenceYears != null && (
                  <>Edad metabólica {liveResults.metabolicAge.differenceYears > 0 ? `${liveResults.metabolicAge.differenceYears} años mayor` : liveResults.metabolicAge.differenceYears < 0 ? `${Math.abs(liveResults.metabolicAge.differenceYears)} años menor` : 'igual'} que la cronológica. </>
                )}
                {liveResults.visceralFat.source === 'derived' && <>Grasa visceral estimada a partir de la masa grasa y la cintura. </>}
                {liveResults.visceralFat.source === 'manual' && <>Grasa visceral capturada manualmente. </>}
                Masa muscular estimada a partir de la masa magra.
              </p>
            </Section>

            <Section title="Requerimiento energético y metas" icon={<Calculator className="h-4 w-4 text-primary" />} defaultOpen>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Nivel de actividad</Label>
                  <Select value={progressForm.activity_level} onValueChange={(v) => setProgressForm((p) => ({ ...p, activity_level: v as ActivityLevel }))} disabled={isSaving}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ACTIVITY_LABELS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Objetivo</Label>
                  <Select value={progressForm.goal} onValueChange={(v) => setProgressForm((p) => ({ ...p, goal: v as EnergyGoal }))} disabled={isSaving}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(GOAL_LABELS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="rounded-lg bg-muted/40 p-2 space-y-1">
                  <p className="text-xs text-muted-foreground">TMB / GET</p>
                  <p className="text-sm font-semibold">
                    {fmt(liveResults.energy.bmr)} / {fmt(liveResults.energy.tdee)} kcal
                    {liveResults.energy.bmrFormulaUsed && (
                      <span className="text-xs text-muted-foreground"> ({liveResults.energy.bmrFormulaUsed === 'katch' ? 'Katch-McArdle' : 'Mifflin-St Jeor'})</span>
                    )}
                  </p>
                </div>
              </div>
              {!liveResults.energy.ok && liveResults.energy.missing.length > 0 && (
                <p className="text-xs text-amber-600">Faltan datos para el cálculo energético: peso, talla, edad y sexo.</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs">Calorías objetivo</Label><Input type="number" value={effectiveTargets.target_calories} onChange={(e) => setTargets((t) => ({ ...t, target_calories: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Proteína (g)</Label><Input type="number" value={effectiveTargets.target_protein} onChange={(e) => setTargets((t) => ({ ...t, target_protein: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Carbs (g)</Label><Input type="number" value={effectiveTargets.target_carbs} onChange={(e) => setTargets((t) => ({ ...t, target_carbs: e.target.value }))} disabled={isSaving} /></div>
                <div className="space-y-1"><Label className="text-xs">Grasas (g)</Label><Input type="number" value={effectiveTargets.target_fat} onChange={(e) => setTargets((t) => ({ ...t, target_fat: e.target.value }))} disabled={isSaving} /></div>
              </div>
            </Section>

            <Section title="Seguimiento" icon={<TrendingUp className="h-4 w-4 text-primary" />}>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Adherencia al plan (%)</Label><Input type="number" min="0" max="100" value={progressForm.adherence_score} onChange={(e) => setProgressForm((p) => ({ ...p, adherence_score: e.target.value }))} disabled={isSaving} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Notas</Label><Textarea value={progressForm.notes} onChange={(e) => setProgressForm((p) => ({ ...p, notes: e.target.value }))} rows={2} disabled={isSaving} /></div>
            </Section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsProgressDialogOpen(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={saveProgress} variant="gradient" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingProgress ? 'Guardar Cambios' : 'Registrar Evaluación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- APPOINTMENT DIALOG ---- */}
      <Dialog open={isApptDialogOpen} onOpenChange={setIsApptDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingAppt ? 'Editar Consulta' : 'Nueva Consulta'}</DialogTitle>
            <DialogDescription>Programa una consulta nutricional.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Cliente *</Label>
              <Select value={apptForm.client_id || 'none'} onValueChange={(v) => setApptForm((p) => ({ ...p, client_id: v === 'none' ? '' : v }))} disabled={isSaving}>
                <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {activeClients.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fecha y hora *</Label>
              <Input type="datetime-local" value={apptForm.scheduled_at} onChange={(e) => setApptForm((p) => ({ ...p, scheduled_at: e.target.value }))} disabled={isSaving} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Tipo</Label>
                <Input value={apptForm.type} onChange={(e) => setApptForm((p) => ({ ...p, type: e.target.value }))} placeholder="Ej: Primera consulta" disabled={isSaving} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select value={apptForm.status} onValueChange={(v) => setApptForm((p) => ({ ...p, status: v }))} disabled={isSaving}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(APPT_STATUS).map(([k, label]) => (<SelectItem key={k} value={k}>{label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notas</Label>
              <Textarea rows={2} value={apptForm.notes} onChange={(e) => setApptForm((p) => ({ ...p, notes: e.target.value }))} disabled={isSaving} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsApptDialogOpen(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={saveAppt} variant="gradient" disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingAppt ? 'Guardar Cambios' : 'Programar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- DISPONIBILIDAD DIALOG ---- */}
      <NutritionAvailabilityDialog
        open={isAvailabilityOpen}
        onOpenChange={setIsAvailabilityOpen}
        availability={availability}
        blocks={availabilityBlocks}
        isSaving={isSaving}
        onSaveAvailability={saveAvailability}
        onDeleteAvailability={removeAvailability}
        onAddBlock={addAvailabilityBlock}
        onDeleteBlock={removeAvailabilityBlock}
      />

      {/* ---- CANCELAR CITA DIALOG ---- */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar cita</DialogTitle>
            <DialogDescription>
              {cancelTarget
                ? `${cancelTarget.client_name || cancelTarget.contact_name || 'Cliente'} · ${new Date(
                    cancelTarget.scheduled_at
                  ).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 py-2">
            <Label className="text-xs">Motivo de la cancelación</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ej: la clienta reprogramó, feriado, etc."
              disabled={isSaving}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={isSaving}>
              Volver
            </Button>
            <Button variant="destructive" onClick={cancelAppt} disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Cancelar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
